import React, { useId, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

export interface StepChartPoint {
  x: number;
  y: number;
}

export interface StepChartWithAverageProps {
  points: StepChartPoint[];
  color: 'time' | 'quality';
  valueLabel: (y: number) => string;
  average: number | null;
  maxPoints?: number;
  onClick?: () => void;
  averageColor?: string;
  averagePrefix?: React.ReactNode;
  averageSuffix?: React.ReactNode;
  averageSlotWidth?: number;
}

const CHART_WIDTH = 100;
const CHART_HEIGHT = 36;
const PAD = 4;

export const StepChartWithAverage: React.FC<StepChartWithAverageProps> = ({
  points,
  color,
  valueLabel,
  average,
  maxPoints,
  onClick,
  averageColor,
  averagePrefix,
  averageSuffix,
  averageSlotWidth = 72,
}) => {
  const [tooltip, setTooltip] = useState<{ clientX: number; clientY: number; text: string; lineX: number } | null>(
    null
  );
  const svgRef = useRef<SVGSVGElement>(null);
  const gradientId = `step-chart-${color}-${useId().replace(/:/g, '')}`;

  const displayPoints = maxPoints ? points.slice(-maxPoints) : points;
  if (displayPoints.length === 0) return null;

  const xs = displayPoints.map((p) => p.x);
  const ys = displayPoints.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(0, ...ys);
  const maxY = Math.max(0, ...ys);
  const rangeX = maxX - minX || 1;
  const rangeY = maxY - minY || 1;
  const w = CHART_WIDTH - PAD * 2;
  const h = CHART_HEIGHT - PAD * 2;

  const toSvg = (px: number, py: number) => ({
    x: PAD + ((px - minX) / rangeX) * w,
    y: PAD + h - ((py - minY) / rangeY) * h,
  });

  // Step path: M x0 y0 L x1 y0 L x1 y1 L x2 y1 ...
  let stepPathD = '';
  for (let i = 0; i < displayPoints.length; i++) {
    const { x: sx, y: sy } = toSvg(displayPoints[i].x, displayPoints[i].y);
    if (i === 0) stepPathD = `M ${sx} ${sy}`;
    else {
      const prevY = toSvg(displayPoints[i - 1].x, displayPoints[i - 1].y).y;
      stepPathD += ` L ${sx} ${prevY} L ${sx} ${sy}`;
    }
  }
  const lastX = toSvg(displayPoints[displayPoints.length - 1].x, displayPoints[displayPoints.length - 1].y).x;
  const firstX = toSvg(displayPoints[0].x, displayPoints[0].y).x;
  const bottomY = PAD + h;
  const zeroY = toSvg(minX, 0).y;
  const areaPathD = `${stepPathD} L ${lastX} ${bottomY} L ${firstX} ${bottomY} Z`;

  const strokeColor = color === 'time' ? '#22034f' : '#876ce9';

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = rect.width > 0 ? CHART_WIDTH / rect.width : 1;
    const xInViewBox = (e.clientX - rect.left) * scaleX;
    const cursorSvgX = Math.max(PAD, Math.min(CHART_WIDTH - PAD, xInViewBox));
    let stepIndex = 0;
    for (let i = 1; i < displayPoints.length; i++) {
      const stepBoundaryX = toSvg(displayPoints[i].x, displayPoints[i].y).x;
      if (cursorSvgX >= stepBoundaryX) stepIndex = i;
      else break;
    }
    const activePoint = displayPoints[stepIndex];
    setTooltip({
      clientX: e.clientX,
      clientY: e.clientY,
      text: `Profile ${activePoint.x}: ${valueLabel(activePoint.y)}`,
      lineX: cursorSvgX,
    });
  };
  const handleMouseLeave = () => setTooltip(null);

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      {average != null && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: averageSlotWidth,
            minWidth: averageSlotWidth,
            mr: 0.5,
            gap: 0.25,
          }}
        >
          {averagePrefix}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: averageColor || strokeColor,
              whiteSpace: 'nowrap',
            }}
          >
            {valueLabel(average)}
          </Typography>
          {averageSuffix}
        </Box>
      )}
      <svg
        ref={svgRef}
        width={CHART_WIDTH}
        height={CHART_HEIGHT}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={onClick}
        style={{ display: 'block', cursor: onClick ? 'pointer' : 'default' }}
      >
        <defs>
          <linearGradient
            id={gradientId}
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={strokeColor} stopOpacity={0.4} />
            <stop offset="100%" stopColor={strokeColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={areaPathD} fill={`url(#${gradientId})`} />
        {tooltip && (
          <line
            x1={tooltip.lineX}
            y1={PAD}
            x2={tooltip.lineX}
            y2={CHART_HEIGHT - PAD}
            stroke="rgba(0, 0, 0, 0.18)"
            strokeWidth={1}
          />
        )}
        <line
          x1={PAD}
          y1={zeroY}
          x2={CHART_WIDTH - PAD}
          y2={zeroY}
          stroke="rgba(0, 0, 0, 0.18)"
          strokeWidth={1}
        />
        <path
          d={stepPathD}
          fill="none"
          stroke={strokeColor}
          strokeWidth={2}
          strokeLinecap="square"
          strokeLinejoin="miter"
        />
      </svg>
      {tooltip && (
        <Box
          sx={{
            position: 'fixed',
            left: tooltip.clientX + 12,
            top: tooltip.clientY - 8,
            bgcolor: 'grey.800',
            color: 'white',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            typography: 'caption',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          {tooltip.text}
        </Box>
      )}
    </Box>
  );
};
